// src/pages/BlogPost.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { URL_PREFIX, PILLAR_SLUGS, TREE_LANG } from '../langPath';
import { useParams, Link } from 'react-router-dom';
import StructuredData from '../components/SEO/StructuredData';
import PageMeta from '../components/SEO/PageMeta';

const BlogPost = () => {
    const { t, i18n } = useTranslation();
    const { slug } = useParams();
    const currentLang = i18n.language.split('-')[0];
    const baseUrl = 'https://robomindsystems.ai';

    // Get blog post by slug - in a real app, this would fetch from API/CMS
    const blogPosts = t('blog.posts', { returnObjects: true }) || [];
    const post = Array.isArray(blogPosts) ? blogPosts.find(p => p.slug === slug) : null;

    if (!post) {
        return (
            <div className="bg-white py-12">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
                    <Link to="/blog" className="text-primary hover:text-primary-hover">
                        ← {t('blog.back_to_blog')}
                    </Link>
                </div>
            </div>
        );
    }

    // The three posts that follow this one in the list (wrapping around), so the
    // links are stable between builds and spread evenly across all articles.
    const idx = blogPosts.findIndex((p) => p.slug === slug);
    const relatedPosts = blogPosts.length > 1
        ? [1, 2, 3].map((n) => blogPosts[(idx + n) % blogPosts.length])
            .filter((p, i, arr) => p.slug !== slug && arr.findIndex((x) => x.slug === p.slug) === i)
        : [];

    // Article Schema for SEO
    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "inLanguage": TREE_LANG,
        "description": post.excerpt,
        "image": post.image ? `${baseUrl}${post.image}` : `${baseUrl}/robomind-og-image.png`,
        "datePublished": post.date,
        "dateModified": post.dateModified || post.date,
        "author": {
            "@type": "Organization",
            "name": "ROBOMIND Systems"
        },
        "publisher": {
            "@type": "Organization",
            "name": "ROBOMIND Systems",
            "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/robomind-logo.png`
            }
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `${baseUrl}${URL_PREFIX}/blog/${slug}`
        }
    };

    return (
        <>
            <PageMeta
                title={post.title.length > 45 ? post.title : `${post.title} | ROBOMIND Blog`}
                description={post.excerpt}
                path={`/blog/${slug}`}
                ogType="article"
                image={post.image || null}
                article={{ published: post.date, modified: post.dateModified || post.date }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
            />

            <StructuredData type="blog" page={{ name: post.title, path: `/blog/${slug}` }} />

            <article className="bg-white py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Back Link */}
                    <Link 
                        to="/blog" 
                        className="text-primary hover:text-primary-hover inline-flex items-center mb-6"
                    >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        {t('blog.back_to_blog')}
                    </Link>

                    {/* Header */}
                    <header className="mb-8">
                        <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl mb-4">
                            {post.title}
                        </h1>
                        <div className="flex items-center text-sm text-gray-500">
                            <time dateTime={post.date}>
                                {new Date(post.date).toLocaleDateString(
                                    currentLang === 'hu' ? 'hu-HU' : 
                                    currentLang === 'ro' ? 'ro-RO' : 
                                    currentLang === 'de' ? 'de-DE' : 'en-US',
                                    { year: 'numeric', month: 'long', day: 'numeric' }
                                )}
                            </time>
                        </div>
                    </header>

                    {/* Featured Image */}
                    {post.image && (
                        <div className="mb-8">
                            <img 
                                src={post.image} 
                                alt={post.title}
                                className="w-full h-auto rounded-lg"
                            />
                        </div>
                    )}

                    {/* Content */}
                    <div className="prose prose-lg max-w-none">
                        {post.content && (
                            <div 
                                className="text-gray-700 leading-relaxed text-justify"
                                dangerouslySetInnerHTML={{ __html: post.content }}
                            />
                        )}
                    </div>

                    {/* LinkedIn Embed Section (if post has LinkedIn URL) */}
                    {post.linkedinUrl && (
                        <div className="mt-12 p-6 bg-gray-50 rounded-lg">
                            <h2 className="text-xl font-bold mb-4">{t('blog.linkedin_post')}</h2>
                            <LinkedInEmbed url={post.linkedinUrl} />
                        </div>
                    )}
                </div>
            {/* Related posts - gives every article internal links beyond the blog index */}
            {relatedPosts.length > 0 && (
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('blog.related_posts')}</h2>
                    <ul className="grid gap-4 sm:grid-cols-3">
                        {relatedPosts.map((p) => (
                            <li key={p.slug} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <Link to={`/blog/${p.slug}`} className="text-primary font-semibold hover:underline">
                                    {p.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Topic pillar link - each tree links to its own language's pillar */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <p className="text-gray-700">
                        {t('pillar_ai.related_prefix')}{' '}
                        <Link to={PILLAR_SLUGS[TREE_LANG]} className="text-primary font-semibold hover:underline">
                            {t('pillar_ai.h1')}
                        </Link>
                    </p>
                </div>
            </div>

            </article>
        </>
    );
};

// LinkedIn Embed Component (Cookie-free, using oEmbed or iframe)
const LinkedInEmbed = ({ url }) => {
    // LinkedIn oEmbed API - no cookies required
    // Note: LinkedIn requires authentication for oEmbed, so we'll use a simple link display
    // For full embed, you'd need to use LinkedIn's Post Embed feature (requires their JS SDK, but can be configured to be privacy-friendly)
    
    return (
        <div className="border border-gray-300 rounded-lg p-4 bg-white">
            <p className="text-sm text-gray-600 mb-2">
                View this post on LinkedIn:
            </p>
            <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-primary-hover font-semibold inline-flex items-center"
            >
                Open LinkedIn Post
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
            </a>
        </div>
    );
};

export default BlogPost;

